import './style.modules.scss'

// (moved above)
import { Loading } from '@ampcore/adel-react'
import {
  AdvisorAttributes,
  AdvisorDataService,
  FaceProfileData,
  VisitorInfoService
} from '@ampcore/client-side-services'
import Cookies from 'js-cookie'
import React, { useEffect, useState } from 'react'

import { useAmpUserStatus } from '../../hooks/useAmpUserStatus'
import { UserStatus } from '../../types/UserStatus.enum'
import {
  DynamicAdvisorClient,
  DynamicAdvisorClientProps
} from './DynamicAdvisorClient'
import {
  DynamicAdvisorPersonalizedProspect,
  DynamicAdvisorPersonalizedProspectProps
} from './DynamicAdvisorPersonalizedProspect'
import {
  DynamicAdvisorProspect,
  DynamicAdvisorProspectProps
} from './DynamicAdvisorProspect'

export type DynamicAdvisorSharedProps = {
  faceProfileData?: FaceProfileData
  advisorAttributes?: AdvisorAttributes
  loading?: boolean
  error?: Error | null
}

export type DynamicAdvisorProps = (
  | DynamicAdvisorClientProps
  | DynamicAdvisorPersonalizedProspectProps
  | DynamicAdvisorProspectProps
) &
  DynamicAdvisorSharedProps

export const DynamicAdvisor: React.FC<DynamicAdvisorProps> = props => {
  const {
    faceProfileData: propsFaceProfileData,
    advisorAttributes: propsAdvisorAttributes,
    loading: propsLoading,
    error: propsError,
    testId
  } = props
  const userStatus = useAmpUserStatus()
  const faceProfileCookie = Cookies.get('FaceProfileCookie')

  const [internalFaceProfileData, setInternalFaceProfileData] = useState<
    FaceProfileData | undefined
  >()
  const [internalAdvisorAttributes, setInternalAdvisorAttributes] = useState<
    AdvisorAttributes | undefined
  >()
  const [internalLoading, setInternalLoading] = useState(false)
  const [internalError, setInternalError] = useState<Error | null>(null)

  // On /search page, default to hidden; elsewhere, default to visible
  const [visible, setVisible] = useState(() => {
    const isSearchPage =
      globalThis?.window?.location?.pathname?.startsWith('/search')
    return isSearchPage ? false : true
  })

  // Prefer injected props, fallback to internal state
  const faceProfileData = propsFaceProfileData ?? internalFaceProfileData
  const advisorAttributes = propsAdvisorAttributes ?? internalAdvisorAttributes
  const loading = propsLoading ?? internalLoading
  const error = propsError ?? internalError

  useEffect(() => {
    const urlParams = new URLSearchParams(
      globalThis?.window?.location?.search || ''
    )
    const isSearchPage =
      globalThis?.window?.location?.pathname?.startsWith('/search')
    // Hide if ?dac=no
    if (urlParams?.get('dac') === 'no') {
      setVisible(false)
      return
    }
    // On /search page, show only after 'no-search-results' event
    if (!isSearchPage) return
    const handleNoSearchResults = () => {
      setVisible(true)
    }
    globalThis.window.addEventListener(
      'no-search-results',
      handleNoSearchResults
    )
    return () => {
      globalThis.window.removeEventListener(
        'no-search-results',
        handleNoSearchResults
      )
    }
  }, [])

  useEffect(() => {
    if (
      propsFaceProfileData !== undefined ||
      propsAdvisorAttributes !== undefined ||
      propsLoading !== undefined ||
      propsError !== undefined
    ) {
      // If injected, skip internal fetch
      return
    }

    if (
      faceProfileCookie &&
      (userStatus == UserStatus.CLIENT_ACTIVE ||
        userStatus == UserStatus.CLIENT_EXPIRED)
    ) {
      const fetchData = async () => {
        setInternalLoading(true)
        setInternalError(null)
        try {
          const advisorDataService = new AdvisorDataService()
          const faceProfileResponse =
            await advisorDataService.getById(faceProfileCookie)
          setInternalFaceProfileData(faceProfileResponse.data ?? undefined)
        } catch (e) {
          setInternalError(e instanceof Error ? e : new Error('Unknown error'))
        } finally {
          setInternalLoading(false)
        }
      }
      fetchData()
    }

    if (userStatus === UserStatus.PROSPECT) {
      const fetchData = async () => {
        setInternalLoading(true)
        setInternalError(null)
        try {
          const result = await VisitorInfoService.getInstance().fetch()
          const data =
            result?.visitor?.targetingData?.advisorcollector?.extraData
              ?.attributes
          setInternalAdvisorAttributes({
            image: data?.image,
            name: data?.name,
            phonenumber: data?.phonenumber,
            tagline: data?.tagline,
            url: data?.url,
            email: data?.email
          })
        } catch (e) {
          setInternalError(e instanceof Error ? e : new Error('Unknown error'))
        } finally {
          setInternalLoading(false)
        }
      }
      fetchData()
    }
  }, [
    faceProfileCookie,
    userStatus,
    propsFaceProfileData,
    propsAdvisorAttributes,
    propsLoading,
    propsError
  ])

  if (!visible) {
    return null
  }

  if (loading) {
    return (
      <div className="u-posRelative u-marginCenter u-padding48">
        <Loading
          className="u-posAbsoluteCenter u-textColorBrand"
          testId={testId}
        />
      </div>
    )
  }

  if (error) {
    // fallback to non-personalized prospect view on data fetch error
    return (
      <DynamicAdvisorProspect {...(props as DynamicAdvisorProspectProps)} />
    )
  }

  if (
    (userStatus === UserStatus.CLIENT_ACTIVE ||
      userStatus === UserStatus.CLIENT_EXPIRED) &&
    faceProfileData
  ) {
    return (
      <DynamicAdvisorClient
        {...(props as DynamicAdvisorClientProps)}
        faceProfileData={faceProfileData}
      />
    )
  }

  if (
    userStatus === UserStatus.PROSPECT &&
    advisorAttributes?.name !== undefined
  ) {
    return (
      <DynamicAdvisorPersonalizedProspect
        {...(props as DynamicAdvisorPersonalizedProspectProps)}
        advisorAttributes={advisorAttributes}
      />
    )
  }

  return <DynamicAdvisorProspect {...(props as DynamicAdvisorProspectProps)} />
}
